import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import MasterPageBreadcrumb from "../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../layout/master/masterRouteMeta";
import MasterStatCard from "../../components/Master/MasterStatCard";
import { MASTER_HOME } from "../../helpers/masterUser";
import MasterSpinner from "../../components/Master/MasterSpinner";
import { ROLES } from "../../constants/roles";
import { isCompanyActive } from "../../helpers/companyActive";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
			<polyline points="22 4 12 14.01 9 11.01" />
		</svg>
	);
}

function IconX() {
	return (
		<svg className="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="10" />
			<line x1="15" y1="9" x2="9" y2="15" />
			<line x1="9" y1="9" x2="15" y2="15" />
		</svg>
	);
}

function IconChevronLeft() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconUsers() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

function IconBuilding() {
	return (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3" />
		</svg>
	);
}

function userInitials(name) {
	if (!name) return "?";
	const parts = String(name).trim().split(/\s+/).filter(Boolean);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
}

function profileLabel(profile) {
	const key = `master.pages.profile_${profile}`;
	const t = i18n.t(key);
	return t !== key ? t : profile || "—";
}

function profileBadgeClass(profile) {
	if (profile === ROLES.master) return "bg-info/10 text-info";
	if (profile === ROLES.admin) return "bg-primary/10 text-primary";
	return "bg-secondary/10 text-secondary";
}

export default function SearchUsersByCompany() {
	const history = useHistory();
	const { user, loading: authLoading } = useContext(AuthContext);
	const [companies, setCompanies] = useState([]);
	const [loadingCompanies, setLoadingCompanies] = useState(true);
	const [companyId, setCompanyId] = useState("");
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [users, setUsers] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [companyName, setCompanyName] = useState("");

	const routeMeta = getMasterRouteMeta(`${MASTER_HOME}/busca/usuarios-por-empresa`);

	const selectedCompany = useMemo(
		() => companies.find((c) => String(c.id) === String(companyId)),
		[companies, companyId]
	);

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/admin-master");
			return;
		}
		api
			.get("/master/companies")
			.then(({ data }) => setCompanies(data || []))
			.catch((err) => toastError(err))
			.finally(() => setLoadingCompanies(false));
	}, [user, authLoading, history]);

	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [search]);

	useEffect(() => {
		setPage(1);
	}, [companyId, debouncedSearch]);

	useEffect(() => {
		if (!companyId) {
			setUsers([]);
			setTotal(0);
			setCompanyName("");
			return;
		}
		setLoading(true);
		api
			.get(`/master/companies/${companyId}/users`, {
				params: {
					pageNumber: page,
					searchParam: debouncedSearch || undefined,
				},
			})
			.then(({ data }) => {
				setUsers(data.users || []);
				setTotal(data.count ?? 0);
				setCompanyName(data.companyName || "");
			})
			.catch((err) => {
				toastError(err);
				setUsers([]);
				setTotal(0);
			})
			.finally(() => setLoading(false));
	}, [companyId, page, debouncedSearch]);

	if (authLoading || !user || user.profile !== ROLES.master) return null;

	const formatDate = (dateStr) => {
		if (!dateStr) return "—";
		return new Date(dateStr).toLocaleDateString("pt-BR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	};

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, page - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	const rangeStart = total ? (page - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(page * PAGE_SIZE, total);

	return (
		<div className="mx-auto w-full max-w-7xl box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>
			{routeMeta.subtitleKey && (
				<p className="-mt-2 mb-6 text-sm text-default-500 dark:text-default-400">
					{i18n.t(routeMeta.subtitleKey)}
				</p>
			)}

			{selectedCompany && (
				<div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
					<MasterStatCard
						tone="primary"
						icon={<IconUsers />}
						value={total}
						label={i18n.t("master.pages.searchUsersStatTotal")}
					/>
					<MasterStatCard
						tone="info"
						icon={<IconBuilding />}
						value={selectedCompany.userLimit ?? "—"}
						label={i18n.t("master.pages.colLimit")}
					/>
					<MasterStatCard
						tone="secondary"
						icon={<IconUsers />}
						value={selectedCompany.connectionCount ?? 0}
						label={i18n.t("master.pages.colConnections")}
					/>
				</div>
			)}

			<div className="card">
				<div className="card-header flex-wrap gap-3">
					<div className="min-w-0">
						<h6 className="card-title m-0">{i18n.t("master.pages.searchUsersListTitle")}</h6>
						{companyName && (
							<p className="m-0 mt-1 text-sm text-default-500">
								{companyName}
								{!isCompanyActive(selectedCompany?.isActive) && (
									<span className="ms-2 inline-flex rounded px-2 py-0.5 text-xs font-medium bg-danger/10 text-danger">
										{i18n.t("master.pages.statusInactive")}
									</span>
								)}
							</p>
						)}
					</div>
					{companyId && (
						<span className="text-sm text-default-500">
							{total} {i18n.t("master.pages.searchUsersCountLabel")}
						</span>
					)}
				</div>

				<div className="card-body border-b border-default-200 pb-6 dark:border-default-700">
					<div className="flex w-full flex-col gap-4 md:flex-row md:items-end">
						<div className="min-w-[200px] md:w-72">
							<label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("master.pages.searchUsersCompanyLabel")}
							</label>
							<select
								value={companyId}
								onChange={(e) => setCompanyId(e.target.value)}
								className="form-select form-input-sm w-full"
								disabled={loadingCompanies}
							>
								<option value="">{i18n.t("master.pages.searchUsersSelectCompany")}</option>
								{companies.map((c) => (
									<option key={c.id} value={c.id}>
										{c.name || `Empresa ${c.id}`}
										{!isCompanyActive(c.isActive) ? ` (${i18n.t("master.pages.statusInactive")})` : ""}
									</option>
								))}
							</select>
						</div>
						<div className="flex-1 md:max-w-xs">
							<label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-default-500">
								{i18n.t("master.pages.searchUsersSearchLabel")}
							</label>
							<div className="relative">
								<input
									type="search"
									className="form-input form-input-sm w-full ps-9"
									placeholder={i18n.t("master.pages.searchUsersPlaceholder")}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									disabled={!companyId}
								/>
								<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
									<IconSearch />
								</div>
							</div>
						</div>
					</div>
				</div>

				{!companyId ? (
					<div className="flex flex-col items-center justify-center gap-2 px-6 pb-8 pt-6 text-center">
						<p className="m-0 font-semibold text-default-800 dark:text-default-100">
							{i18n.t("master.pages.searchUsersNoCompany")}
						</p>
						<p className="m-0 text-sm text-default-500">
							{i18n.t("master.pages.searchUsersNoCompanyHint")}
						</p>
					</div>
				) : loading && users.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-4 px-6 pb-8 pt-6 text-default-500">
						<MasterSpinner />
						<span>{i18n.t("master.pages.searchUsersLoading")}</span>
					</div>
				) : users.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-2 px-6 pb-8 pt-6 text-center">
						<p className="m-0 font-semibold text-default-800 dark:text-default-100">
							{i18n.t("master.pages.searchUsersEmpty")}
						</p>
						{debouncedSearch && (
							<p className="m-0 text-sm text-default-500">
								{i18n.t("master.pages.searchUsersEmptyHint")}
							</p>
						)}
					</div>
				) : (
					<div className="flex flex-col pt-5">
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
								<thead className="bg-default-150 dark:bg-default-800/60">
									<tr className="whitespace-nowrap text-sm font-normal text-default-700 dark:text-default-300">
										<th className="px-3.5 py-3 text-start">ID</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("master.pages.colUser")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("master.pages.colEmail")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("master.pages.colProfile")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("master.pages.colStatus")}
										</th>
										<th className="px-3.5 py-3 text-start">
											{i18n.t("master.pages.colCreated")}
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-default-200 dark:divide-default-700">
									{users.map((u) => {
										const isActive = u.status === "active";
										return (
											<tr
												key={u.id}
												className="whitespace-nowrap text-sm text-default-800 dark:text-default-200"
											>
												<td className="px-3.5 py-3 font-medium text-primary">#{u.id}</td>
												<td className="px-3.5 py-3">
													<div className="flex items-center gap-3">
														<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-default-200 text-sm font-semibold text-default-700 dark:bg-default-700 dark:text-default-200">
															{userInitials(u.name)}
														</div>
														<div className="min-w-0">
															<p className="m-0 font-semibold text-default-800 dark:text-default-100">
																{u.name || "—"}
															</p>
															{u.isCompanyOwner && (
																<span className="mt-0.5 inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary">
																	{i18n.t("master.pages.companyOwner")}
																</span>
															)}
														</div>
													</div>
												</td>
												<td className="px-3.5 py-3 text-default-600 dark:text-default-400">
													{u.email || "—"}
												</td>
												<td className="px-3.5 py-3">
													<span
														className={`inline-flex rounded px-2.5 py-0.5 text-xs font-medium ${profileBadgeClass(
															u.profile
														)}`}
													>
														{profileLabel(u.profile)}
													</span>
												</td>
												<td className="px-3.5 py-3">
													{isActive ? (
														<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-success/10 text-success">
															<IconCheck />
															{i18n.t("master.pages.statusActive")}
														</span>
													) : (
														<span className="inline-flex items-center gap-x-1 rounded px-2.5 py-0.5 text-xs font-medium bg-danger/10 text-danger">
															<IconX />
															{i18n.t("master.pages.statusInactive")}
														</span>
													)}
												</td>
												<td className="px-3.5 py-3 text-default-600 dark:text-default-400">
													{formatDate(u.createdAt)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>

						{total > 0 && (
							<div className="card-footer">
								<p className="m-0 text-sm text-default-500">
									{i18n.t("master.pages.searchUsersShowing", {
										start: rangeStart,
										end: rangeEnd,
										total,
									})}
								</p>
								<nav className="flex flex-wrap items-center gap-2" aria-label="Pagination">
									<button
										type="button"
										className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
										disabled={page <= 1 || loading}
										onClick={() => setPage((p) => Math.max(1, p - 1))}
									>
										<IconChevronLeft />
										<span className="ms-1">{i18n.t("master.pages.previous")}</span>
									</button>
									{pageNumbers().map((p) => (
										<button
											key={p}
											type="button"
											className={`btn size-7 border ${
												p === page
													? "border-primary bg-primary text-white"
													: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
											}`}
											disabled={loading}
											onClick={() => setPage(p)}
										>
											{p}
										</button>
									))}
									<button
										type="button"
										className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
										disabled={page >= totalPages || loading}
										onClick={() => setPage((p) => p + 1)}
									>
										<span className="me-1">{i18n.t("master.pages.next")}</span>
										<IconChevronRight />
									</button>
								</nav>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
